import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import * as repo from '@/db/repositories'
import { createId } from '@/utils/id'
import type { ReadingSession } from '@/types/domain'
import { track } from '@/composables/useEventTracker'

/**
 * Store de estadísticas de lectura.
 *
 * Registra sesiones (tiempo + progreso por sesión) y expone métricas
 * derivadas: tiempo total, por día, racha, libros leídos, etc.
 *
 * El registro de sesiones lo dispara el ReaderView: al abrir un libro
 * se inicia un "timer", y al cerrar (o periódicamente) se guarda una
 * sesión con la duración acumulada.
 */
export const useStatsStore = defineStore('stats', () => {
  const sessions = ref<ReadingSession[]>([])

  // --- Sesión en curso (timer) ---
  let activeSession: {
    bookId: string
    startedAt: number
    percentStart: number
    lastTickAt: number
  } | null = null
  /**
   * Segundos transcurridos de la sesión activa, reactivo. Lo actualiza
   * `tick()` (llamado periódicamente desde el lector) para que el anillo
   * de meta diaria refleje el tiempo en vivo, no solo al cerrar sesión.
   */
  const liveSessionSeconds = ref(0)

  /**
   * Libro de la sesión activa, o null. Existe para que el reproductor de
   * audiolibro sepa si ya hay una sesión abierta: `activeSession` es una
   * variable de cierre privada y sin esto el tiempo se contaría dos veces
   * al escuchar dentro del lector.
   */
  const activeBookId = ref<string | null>(null)

  /** Carga todas las sesiones (para la vista de estadísticas globales). */
  async function loadAll() {
    sessions.value = await repo.getAllSessions()
  }

  /** Inicia el seguimiento de una sesión de lectura. */
  function startSession(bookId: string, currentPercent: number) {
    const now = Date.now()
    activeSession = {
      bookId,
      startedAt: now,
      percentStart: currentPercent,
      lastTickAt: now,
    }
    activeBookId.value = bookId
    liveSessionSeconds.value = 0
  }

  /**
   * Actualiza el contador reactivo de la sesión activa. Lo llama el lector
   * cada ~30s para que la UI (anillo de meta diaria) refleje el progreso
   * sin esperar al cierre de sesión.
   */
  function tick() {
    if (!activeSession) return
    liveSessionSeconds.value = Math.round((Date.now() - activeSession.startedAt) / 1000)
  }

  /**
   * Cierra la sesión activa y la persiste.
   * `currentPercent` es el progreso al momento de cerrar.
   */
  async function endSession(currentPercent: number): Promise<void> {
    if (!activeSession) return
    // La sesión se suelta AQUÍ, antes de persistirla, y se trabaja sobre una
    // copia. `activeBookId` es el guard con el que el lector y el reproductor
    // deciden si abrir una sesión o respetar la que ya hay; dejarlo puesto
    // durante el `await` de la escritura significaba que un `startSession`
    // inmediatamente posterior se saltaba en silencio. Pasa al parar la
    // escucha de un libro y empezar la de otro: el segundo no contaba nada,
    // ni tiempo ni racha, hasta la siguiente vez.
    const open = activeSession
    activeSession = null
    activeBookId.value = null
    const now = new Date()
    const durationSec = Math.round((now.getTime() - open.startedAt) / 1000)
    // Ignorar sesiones triviales (< 10s).
    if (durationSec >= 10) {
      const session: ReadingSession = {
        id: createId(),
        bookId: open.bookId,
        durationSec,
        pagesRead: Math.max(0, Math.round(currentPercent - open.percentStart)),
        percentStart: open.percentStart,
        percentEnd: currentPercent,
        date: now.toISOString().slice(0, 10),
        startedAt: new Date(open.startedAt).toISOString(),
      }
      await repo.saveSession(session)
      sessions.value = [...sessions.value, session]
      // Emitir evento para stats globales (panel admin). Sin contenido sensible.
      void track('reading_session', {
        bookId: open.bookId,
        seconds: durationSec,
        percentEnd: currentPercent,
      })
    }
  }

  // ---------- Métricas derivadas ----------

  /** Tiempo total de lectura en segundos. */
  const totalSeconds = computed(() =>
    sessions.value.reduce((sum, s) => sum + s.durationSec, 0),
  )

  /** Tiempo total formateado (ej. "2h 15m"). */
  const totalReadable = computed(() => formatDuration(totalSeconds.value))

  /** Sesiones agrupadas por día (date → segundos). */
  const byDay = computed(() => {
    const map = new Map<string, number>()
    for (const s of sessions.value) {
      map.set(s.date, (map.get(s.date) ?? 0) + s.durationSec)
    }
    return map
  })

  /** Serie de los últimos N días para un gráfico. */
  function dailySeries(days = 14): { date: string; seconds: number }[] {
    const out: { date: string; seconds: number }[] = []
    const today = new Date()
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today)
      d.setDate(d.getDate() - i)
      const key = d.toISOString().slice(0, 10)
      out.push({ date: key, seconds: byDay.value.get(key) ?? 0 })
    }
    return out
  }

  /** Racha actual de días consecutivos con lectura. */
  const currentStreak = computed(() => {
    if (byDay.value.size === 0) return 0
    let streak = 0
    const d = new Date()
    // Si hoy no hay, empezamos desde ayer (racha "hasta ayer").
    if (!byDay.value.has(d.toISOString().slice(0, 10))) {
      d.setDate(d.getDate() - 1)
    }
    while (byDay.value.has(d.toISOString().slice(0, 10))) {
      streak++
      d.setDate(d.getDate() - 1)
    }
    return streak
  })

  /** Sesiones de un libro concreto. */
  function sessionsForBook(bookId: string): ReadingSession[] {
    return sessions.value.filter((s) => s.bookId === bookId)
  }

  /** Segundos leídos hoy (para el progreso hacia la meta diaria). */
  const todaySeconds = computed(() => {
    const today = new Date().toISOString().slice(0, 10)
    return byDay.value.get(today) ?? 0
  })

  /**
   * Segundos leídos hoy incluyendo la sesión activa en vivo (para el
   * anillo de meta diaria del lector). Combina sesiones ya guardadas con
   * el tiempo transcurrido de la sesión en curso.
   */
  const todaySecondsLive = computed(() => todaySeconds.value + liveSessionSeconds.value)

  return {
    sessions,
    activeBookId,
    totalSeconds,
    totalReadable,
    byDay,
    currentStreak,
    todaySeconds,
    todaySecondsLive,
    loadAll,
    startSession,
    endSession,
    tick,
    dailySeries,
    sessionsForBook,
  }
})

/** Formatea segundos como "2h 15m" o "45m" o "30s". */
export function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`
  const m = Math.floor(sec / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  const remMin = m % 60
  return remMin > 0 ? `${h}h ${remMin}m` : `${h}h`
}
